// src/components/ProfileModal.tsx
import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, Switch, Space, Button, Typography, Tag, Divider, App as AntdApp } from 'antd';
import { SafetyOutlined } from '@ant-design/icons';
import AmPhoneInput from './AmPhoneInput';
import { useAppTheme } from '../theme/AppThemeProvider';
import { amPhoneRule, normalizeAmPhoneStrict } from '../utils/phoneRules';
import {
  getAuthProfile, saveAuthProfile,
  getTotpStatus, setupTotp, confirmTotp, removeTotp,
} from '../services/auth';

export type UserProfile = {
  name?: string;
  phone?: string;
  email?: string;
  locale?: 'ru' | 'hy' | 'en';
  theme?: 'light' | 'dark';
  newPassword?: string;
  confirmPassword?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  initial?: UserProfile;
  onSave: (p: UserProfile) => void;
  onPickAvatar?: () => void;
  avatar?: string | null;
};

const ProfileModal: React.FC<Props> = ({ open, onClose, initial, onSave, onPickAvatar, avatar }) => {
  const [form] = Form.useForm<UserProfile>();
  const { mode, setMode } = useAppTheme();
  const { message } = AntdApp.useApp();

  // ── 2FA / TOTP state ──────────────────────────────────────────────
  const [totpEnabled, setTotpEnabled] = useState<boolean | null>(null);
  const [setupData, setSetupData] = useState<{ secret: string; qrUrl: string } | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [totpBusy, setTotpBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const auth = getAuthProfile();
    form.setFieldsValue({
      name: initial?.name ?? '',
      phone: initial?.phone ?? '',
      email: initial?.email ?? auth.email,
      locale: initial?.locale ?? 'ru',
      theme: initial?.theme ?? mode,
    });
    // reset + load TOTP status
    setSetupData(null);
    setTotpCode('');
    setTotpEnabled(null);
    getTotpStatus().then(setTotpEnabled);
  }, [open, initial, form, mode]);

  const startTotpSetup = async () => {
    setTotpBusy(true);
    try {
      const data = await setupTotp();
      setSetupData(data);
      setTotpCode('');
    } catch (e: any) {
      message.error(e?.message ?? 'Не удалось начать настройку');
    } finally {
      setTotpBusy(false);
    }
  };

  const confirmTotpSetup = async () => {
    if (!/^\d{6}$/.test(totpCode)) { message.warning('Введите 6-значный код'); return; }
    setTotpBusy(true);
    try {
      await confirmTotp(totpCode);
      setTotpEnabled(true);
      setSetupData(null);
      setTotpCode('');
      message.success('Двухфакторная аутентификация включена');
    } catch (e: any) {
      message.error(e?.message ?? 'Неверный код');
    } finally {
      setTotpBusy(false);
    }
  };

  const disableTotp = async () => {
    setTotpBusy(true);
    try {
      await removeTotp();
      setTotpEnabled(false);
      setSetupData(null);
      message.success('Двухфакторная аутентификация отключена');
    } catch (e: any) {
      message.error(e?.message ?? 'Ошибка отключения');
    } finally {
      setTotpBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Профиль"
      okText="Сохранить"
      onOk={async () => {
        const v = await form.validateFields();
        const phoneNormalized = v.phone ? normalizeAmPhoneStrict(v.phone) : null;
        const payload: UserProfile = { ...v, phone: phoneNormalized ?? (v.phone || '') };

        const outgoing: UserProfile = { ...payload };
        delete (outgoing as any).newPassword;
        delete (outgoing as any).confirmPassword;
        onSave(outgoing);

        // смена email / пароля на сервере (admin)
        const patch: Partial<ReturnType<typeof getAuthProfile>> = {};
        if (typeof v.email === 'string' && v.email.trim()) (patch as any).email = v.email.trim();
        if (v.newPassword && v.newPassword.length > 0) (patch as any).password = v.newPassword;
        try {
          await saveAuthProfile(patch);
        } catch (err: any) {
          message.error(err?.message ?? 'Ошибка при сохранении');
          return;
        }

        form.resetFields(['newPassword', 'confirmPassword']);
        onClose();
      }}
      width={560}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              width: 56, height: 56, borderRadius: '50%',
              background: '#f0f0f0', overflow: 'hidden', flex: '0 0 auto',
            }}
          >
            {avatar ? (
              // eslint-disable-next-line jsx-a11y/alt-text
              <img src={avatar} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div style={{ width: '100%', height: '100%' }} />
            )}
          </div>
          {onPickAvatar && <Button onClick={onPickAvatar}>Изменить фото</Button>}
        </div>

        <Form layout="vertical" form={form}>
          <Form.Item label="Имя" name="name">
            <Input placeholder="Как вас называть" />
          </Form.Item>

          <Form.Item label="Телефон" name="phone" rules={[amPhoneRule]}>
            <AmPhoneInput />
          </Form.Item>

          <Form.Item
            label="E-mail (используется при входе)"
            name="email"
            rules={[{ type: 'email', message: 'Неверный e-mail' }]}
          >
            <Input placeholder="you@example.com" />
          </Form.Item>

          <Form.Item label="Язык интерфейса" name="locale">
            <Select
              options={[
                { value: 'ru', label: 'Русский' },
                { value: 'hy', label: 'Հայերեն' },
                { value: 'en', label: 'English' },
              ]}
            />
          </Form.Item>

          <Form.Item label="Тема">
            <Switch
              checked={mode === 'dark'}
              checkedChildren="Dark"
              unCheckedChildren="Light"
              onChange={(checked) => {
                const m = checked ? 'dark' : 'light';
                setMode(m);
                form.setFieldsValue({ theme: m });
              }}
            />
          </Form.Item>

          <Typography.Text type="secondary">Смена пароля</Typography.Text>
          <Form.Item label="Новый пароль" name="newPassword">
            <Input.Password autoComplete="new-password" placeholder="Оставьте пустым — чтобы не менять" />
          </Form.Item>
          <Form.Item
            label="Подтверждение пароля"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const p = getFieldValue('newPassword');
                  if (!p && !value) return Promise.resolve();
                  return value === p ? Promise.resolve() : Promise.reject(new Error('Пароли не совпадают'));
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>

        <Divider style={{ margin: '4px 0' }} />

        {/* ── Двухфакторная аутентификация (Google Authenticator) ── */}
        <div>
          <Space align="center" style={{ marginBottom: 8 }}>
            <SafetyOutlined />
            <Typography.Text strong>Двухфакторная аутентификация</Typography.Text>
            {totpEnabled === true && <Tag color="green">Включена</Tag>}
            {totpEnabled === false && <Tag>Выключена</Tag>}
          </Space>

          {totpEnabled === true && !setupData && (
            <div>
              <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
                При входе потребуется код из Google Authenticator.
              </Typography.Paragraph>
              <Button danger loading={totpBusy} onClick={disableTotp}>Отключить 2FA</Button>
            </div>
          )}

          {totpEnabled === false && !setupData && (
            <div>
              <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
                Добавьте второй фактор — код из приложения Google Authenticator.
              </Typography.Paragraph>
              <Button type="primary" loading={totpBusy} onClick={startTotpSetup}>Настроить 2FA</Button>
            </div>
          )}

          {setupData && (
            <div>
              <Typography.Paragraph style={{ marginBottom: 6 }}>
                1. Открой <b>Google Authenticator</b> → <b>+</b> → <b>Ввести ключ настройки</b>.
                Введи любое имя и этот ключ (Time based):
              </Typography.Paragraph>
              <Input
                readOnly
                value={setupData.secret}
                onFocus={(e) => e.target.select()}
                style={{ fontFamily: 'monospace', marginBottom: 10 }}
              />
              <Typography.Paragraph style={{ marginBottom: 6 }}>
                2. Введи 6-значный код из приложения для подтверждения:
              </Typography.Paragraph>
              <Space>
                <Input
                  placeholder="123456"
                  inputMode="numeric"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  style={{ width: 140 }}
                />
                <Button type="primary" loading={totpBusy} onClick={confirmTotpSetup}>Подтвердить</Button>
                <Button onClick={() => { setSetupData(null); setTotpCode(''); }}>Отмена</Button>
              </Space>
            </div>
          )}
        </div>
      </Space>
    </Modal>
  );
};

export default ProfileModal;
